import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { PaymentProvider } from '../types/PaymentTypes';
import { CARD_PADDING } from '../caseStatusTheme';
import { useCaseStatusLanguage } from '../../../../routes/CaseStatus/CaseStatusLanguageProvider';

type Props = {
  providers: PaymentProvider[];
  isLoading: boolean;
  confirmLoading: boolean;
  totalBalance: string;
  currency: string;
  onConfirm: (provider: PaymentProvider) => void;
  onBack: () => void;
};

const providerLabelKeys: Record<number, { titleKey: string; subtitleKey: string }> = {
  0: { titleKey: 'ProviderCardTitle', subtitleKey: 'ProviderCardSubtitle' },
  1: { titleKey: 'ProviderSwishTitle', subtitleKey: 'ProviderSwishSubtitle' },
  2: { titleKey: 'ProviderNeonomicsTitle', subtitleKey: 'ProviderNeonomicsSubtitle' },
};

const PaymentProviderPicker = ({
  providers,
  isLoading,
  confirmLoading,
  totalBalance,
  currency,
  onConfirm,
  onBack,
}: Props) => {
  const [selectedType, setSelectedType] = React.useState<number | null>(null);
  const { t, fmtCurrency } = useCaseStatusLanguage();

  const selectedProvider = providers.find((p) => p.type === selectedType) ?? null;

  return (
    <Paper elevation={1}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button
          onClick={onBack}
          size="small"
          startIcon={<ArrowBackIcon />}
          sx={{ minWidth: 0, p: 0.5, color: 'text.secondary' }}
          aria-label={t('Back')}
        />
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
          {t('ChoosePaymentMethod')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2.5,
          px: CARD_PADDING,
          py: 1.5,
          bgcolor: 'grey.100',
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('AmountToPay')}
        </Typography>
        <Typography variant="subtitle1" component="p" sx={{ fontWeight: 700 }}>
          {fmtCurrency(totalBalance, currency)}
        </Typography>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!isLoading && providers.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {t('NoPaymentProviders')}
        </Typography>
      )}

      {!isLoading && providers.length > 0 && (
        <RadioGroup
          value={selectedType ?? ''}
          onChange={(e) => setSelectedType(Number(e.target.value))}
          sx={{ gap: 1, mb: 2.5 }}
        >
          {providers.map((provider) => (
            <Box
              key={provider.type}
              onClick={() => setSelectedType(provider.type)}
              sx={{
                border: 1,
                borderColor: selectedType === provider.type ? 'primary.main' : 'divider',
                borderRadius: 1,
                px: 2,
                py: 2,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                transition: 'border-color 0.15s',
              }}
            >
              {provider.logo && (
                <Box
                  component="img"
                  src={provider.logo.replace(/^~\//, '/')}
                  alt={provider.name}
                  sx={{ height: 24, objectFit: 'contain', flexShrink: 0 }}
                />
              )}
              <Box sx={{ flex: 1, textAlign: 'right' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {providerLabelKeys[provider.type] ? t(providerLabelKeys[provider.type].titleKey) : provider.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                  {providerLabelKeys[provider.type] ? t(providerLabelKeys[provider.type].subtitleKey) : ''}
                </Typography>
              </Box>
              <Radio value={provider.type} size="small" sx={{ p: 0, flexShrink: 0 }} />
            </Box>
          ))}
        </RadioGroup>
      )}

      <Button
        variant="contained"
        color="primary"
        fullWidth
        disabled={selectedProvider === null || isLoading || confirmLoading}
        onClick={() => selectedProvider && onConfirm(selectedProvider)}
        startIcon={confirmLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        {t('ContinueToPayment')}
      </Button>
    </Paper>
  );
};

export default PaymentProviderPicker;
